import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Trash2,
  LogOut,
  Save,
  Plus,
  Palette,
  Image as ImageIcon,
  Megaphone,
  Map,
  Trophy,
  Mail,
  Tag,
  Phone,
  ImagePlus,
  BarChart3,
  RotateCcw,
} from "lucide-react";
import { ADMIN } from "@/constants/testIds";
import { AnalyticsPanel } from "@/components/admin/AnalyticsPanel";
import EditPromoDialog from "@/components/admin/EditPromoDialog";
import { useAdminConfig } from "@/components/admin/useAdminConfig";
import PromoForm from "@/components/admin/promos/PromoForm";
import PromoList from "@/components/admin/promos/PromoList";
import AdsTab from "@/components/admin/AdsTab";
import { RevertControls } from "@/components/admin/RevertControls";
import { ScheduledBanners } from "@/components/admin/ScheduledBanners";
import { StylePresets } from "@/components/admin/StylePresets";
import { resolveMediaUrl } from "@/lib/ddfApi";

// Reusable HEX color + swatch editor group (Base / Section bar / Text colors).
function ColorEditorGroup({ title, testid, fields, draft, setDraft, className, placeholder = "#000000" }) {
  const update = (key, v) =>
    setDraft({ ...draft, styleSettings: { ...draft.styleSettings, [key]: v } });
  return (
    <div className={className} data-testid={testid}>
      <Label className="text-sm font-semibold">{title}</Label>
      {fields.map(({ key, label, def, tid }) => {
        const val = draft.styleSettings?.[key] || def;
        return (
          <div key={key} className="flex items-center gap-2">
            <Label className="flex-1 text-xs">{label}</Label>
            <Input
              type="color"
              value={val}
              onChange={(e) => update(key, e.target.value)}
              className="w-12 h-9 p-1"
              data-testid={tid}
            />
            <Input
              type="text"
              value={val}
              onChange={(e) => {
                const v = e.target.value;
                if (/^#[0-9a-fA-F]{0,6}$/.test(v)) update(key, v);
              }}
              placeholder={placeholder}
              className="w-24 h-9 font-mono text-xs"
              maxLength={7}
              data-testid={`${tid}-hex`}
            />
          </div>
        );
      })}
    </div>
  );
}

const BASE_COLOR_FIELDS = [
  { key: "bg", label: "Background", def: "#f5f2eb", tid: ADMIN.bgColor },
  { key: "textColor", label: "Text", def: "#1a1a1a", tid: ADMIN.textColor },
  { key: "barColor", label: "Accent", def: "#c49a6c", tid: ADMIN.barColor },
];
const BAR_COLOR_FIELDS = [
  { key: "depArrBarColor", label: "Departures Promotion", def: "#4a4a4a", tid: "admin-deparr-bar-color" },
  { key: "arrBarColor", label: "Arrivals Promotion", def: "#4a4a4a", tid: "admin-arr-bar-color" },
  { key: "shopMapBarColor", label: "Shop Map", def: "#2c2c2c", tid: "admin-shopmap-bar-color" },
  { key: "drawBarColor", label: "Exclusive Draw", def: "#000000", tid: "admin-draw-bar-color" },
  { key: "categoryBgColor", label: "Category Selection Background", def: "#c49a6c", tid: "admin-category-bg-color" },
];
const BAR_TEXT_COLOR_FIELDS = [
  { key: "depArrBarTextColor", label: "Departures Promotion", def: "#ffffff", tid: "admin-deparr-bar-text-color" },
  { key: "arrBarTextColor", label: "Arrivals Promotion", def: "#ffffff", tid: "admin-arr-bar-text-color" },
  { key: "shopMapBarTextColor", label: "Shop Map", def: "#ffffff", tid: "admin-shopmap-bar-text-color" },
  { key: "drawBarTextColor", label: "Exclusive Draw", def: "#ffffff", tid: "admin-draw-bar-text-color" },
];

export default function AdminPanel({ config, onClose, onChanged }) {
  const {
    draft, setDraft, saving, persist,
    logoFileRef, onSaveLogo,
    bannerFileRef, onSaveBanner,
    bgImgRef, onSaveStyle, onClearBgImage,
    adFileRef, onAddAd, onDeleteAd,
    adForm, setAdForm, adListTab, setAdListTab,
    onUpdateAd, onPublishAdNow, onMoveAdToHolding, promoOptions,
    mapFileRef, onAddMap, onDeleteMap,
    millRef, luxRef, motoRef, onSavePrize,
    regMediaRef, onSaveRegister,
    subBgRef, onSaveSubscribe, subBgLocal, onSubBgChange, onResetSubscribe,
    onSaveContact,
    promoLoc, setPromoLoc, promoListTab, setPromoListTab,
    promoForm, setPromoForm, promoImgRef,
    editing, setEditing, editImgRef,
    batchPublishAt, setBatchPublishAt,
    holdingEmail, setHoldingEmail, emailingHolding, onEmailHolding,
    downloadingHolding, onDownloadHolding, holdingEmailError,
    onResetSection, onScheduleRevert,
    selectedHolding, toggleHoldingSelect, onDuplicateHolding,
    onAddPromo, onRemovePromo, onEditPromo, onSaveEditing,
    onLogout,
    onPublishNow, onChangePublishAt, onScheduleItem, onPublishAllNow, onScheduleAll,
    holdingItems, publishedDepartures, publishedArrivals,
  } = useAdminConfig(config, onChanged, onClose);

  return (
    <>
      <Sheet open={true} onOpenChange={(v) => !v && onClose?.()}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-xl overflow-y-auto"
          data-testid={ADMIN.panel}
        >
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>Admin Control Center</span>
              <Button
                variant="outline"
                size="sm"
                onClick={onLogout}
                data-testid={ADMIN.logoutBtn}
              >
                <LogOut size={14} /> Logout
              </Button>
            </SheetTitle>
            <SheetDescription>
              All changes are global and applied immediately to all visitors.
            </SheetDescription>
          </SheetHeader>

          <Tabs defaultValue="banner" className="mt-4">
            <TabsList className="flex flex-wrap sm:flex-nowrap overflow-x-auto gap-1 h-auto justify-start">
              <TabsTrigger value="logo" data-testid="admin-tab-logo"><ImagePlus size={14} /> Logo</TabsTrigger>
              <TabsTrigger value="banner" data-testid="admin-tab-banner"><ImageIcon size={14} /> Banner</TabsTrigger>
              <TabsTrigger value="style" data-testid="admin-tab-style"><Palette size={14} /> Style</TabsTrigger>
              <TabsTrigger value="ads" data-testid="admin-tab-ads"><Megaphone size={14} /> Ads</TabsTrigger>
              <TabsTrigger value="maps" data-testid="admin-tab-maps"><Map size={14} /> Maps</TabsTrigger>
              <TabsTrigger value="prize" data-testid="admin-tab-prize"><Trophy size={14} /> Prizes</TabsTrigger>
              <TabsTrigger value="reg" data-testid="admin-tab-reg"><Mail size={14} /> Register</TabsTrigger>
              <TabsTrigger value="promo" data-testid="admin-tab-promo"><Tag size={14} /> Promos</TabsTrigger>
              <TabsTrigger value="contact" data-testid="admin-tab-contact"><Phone size={14} /> Contact</TabsTrigger>
              <TabsTrigger value="analytics" data-testid="admin-tab-analytics"><BarChart3 size={14} /> Analytics</TabsTrigger>
            </TabsList>

            {/* Logo */}
            <TabsContent value="logo" className="space-y-3 pt-4">
              <Label>Site Logo (image)</Label>
              <Input
                type="file"
                accept="image/*"
                ref={logoFileRef}
                data-testid={ADMIN.logoUpload}
              />
              {draft.siteLogo?.url && (
                <img
                  src={resolveMediaUrl(draft.siteLogo.url)}
                  alt="logo"
                  className="rounded mt-1 max-h-16 object-contain"
                />
              )}
              <Button
                onClick={onSaveLogo}
                disabled={saving}
                data-testid={ADMIN.saveLogoBtn}
              >
                <Save size={14} /> Save Logo
              </Button>
            </TabsContent>

            {/* Banner */}
            <TabsContent value="banner" className="space-y-3 pt-4">
              <Label>Special Offer Banner (image / video)</Label>
              <Input
                type="file"
                accept="image/*,video/*"
                ref={bannerFileRef}
                data-testid={ADMIN.bannerUpload}
              />
              {draft.specialBanner?.url && draft.specialBanner.type === "video" && (
                <video
                  src={resolveMediaUrl(draft.specialBanner.url)}
                  className="rounded mt-1 max-h-32 object-cover"
                  muted
                  autoPlay
                  loop
                  playsInline
                  data-testid="admin-banner-video-preview"
                />
              )}
              {draft.specialBanner?.url && draft.specialBanner.type !== "video" && (
                <img
                  src={resolveMediaUrl(draft.specialBanner.url)}
                  alt="banner"
                  className="rounded mt-1 max-h-32 object-cover"
                />
              )}
              <Button
                onClick={onSaveBanner}
                disabled={saving}
                data-testid={ADMIN.saveBannerBtn}
              >
                <Save size={14} /> Save Banner
              </Button>
              <RevertControls
                key={`banner-${config.bannerRevertAt || ""}`}
                section="banner"
                label="Banner"
                scheduledAt={config.bannerRevertAt}
                saving={saving}
                onReset={onResetSection}
                onSchedule={onScheduleRevert("bannerRevertAt")}
              />
              <ScheduledBanners draft={draft} setDraft={setDraft} persist={persist} saving={saving} />
            </TabsContent>

            {/* Style */}
            <TabsContent value="style" className="space-y-3 pt-4">
              <ColorEditorGroup
                title="Base Colors"
                testid="admin-base-color-editor"
                fields={BASE_COLOR_FIELDS}
                draft={draft}
                setDraft={setDraft}
                className="space-y-3"
              />
              <ColorEditorGroup
                title="Section Bar Colors"
                testid="admin-bar-color-editor"
                fields={BAR_COLOR_FIELDS}
                draft={draft}
                setDraft={setDraft}
                className="space-y-3 pt-3 border-t"
              />
              <ColorEditorGroup
                title="Section Bar Text Colors"
                testid="admin-bar-text-color-editor"
                fields={BAR_TEXT_COLOR_FIELDS}
                draft={draft}
                setDraft={setDraft}
                className="space-y-3 pt-3 border-t"
                placeholder="#ffffff"
              />

              {/* Background Image */}
              <div className="space-y-2 pt-2 border-t pt-3">
                <Label>Background Image (fits whole screen, no distortion)</Label>
                <Input
                  type="file"
                  accept="image/*"
                  ref={bgImgRef}
                  data-testid="admin-bg-image-upload"
                />
                {draft.styleSettings?.bgImage && (
                  <div className="flex items-center gap-2">
                    <img
                      src={resolveMediaUrl(draft.styleSettings.bgImage)}
                      alt="bg"
                      className="h-16 w-28 object-cover rounded border"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={onClearBgImage}
                      data-testid="admin-bg-image-clear"
                    >
                      <Trash2 size={14} /> Remove
                    </Button>
                  </div>
                )}
              </div>

              <Button
                onClick={onSaveStyle}
                disabled={saving}
                data-testid={ADMIN.saveStyleBtn}
              >
                <Save size={14} /> Update Style
              </Button>
              <RevertControls
                key={`style-${config.styleRevertAt || ""}`}
                section="style"
                label="Style"
                scheduledAt={config.styleRevertAt}
                saving={saving}
                onReset={onResetSection}
                onSchedule={onScheduleRevert("styleRevertAt")}
              />
              <StylePresets draft={draft} setDraft={setDraft} persist={persist} saving={saving} />
            </TabsContent>

            {/* Ads */}
            <TabsContent value="ads" className="space-y-3 pt-4">
              <AdsTab
                draft={draft}
                saving={saving}
                adFileRef={adFileRef}
                adForm={adForm}
                setAdForm={setAdForm}
                promoOptions={promoOptions}
                adListTab={adListTab}
                setAdListTab={setAdListTab}
                onAddAd={onAddAd}
                onDeleteAd={onDeleteAd}
                onUpdateAd={onUpdateAd}
                onPublishAdNow={onPublishAdNow}
                onMoveAdToHolding={onMoveAdToHolding}
              />
            </TabsContent>

            {/* Maps */}
            <TabsContent value="maps" className="space-y-3 pt-4">
              <Label>Shop Map (JPG only)</Label>
              <Input
                type="file"
                accept="image/jpeg"
                ref={mapFileRef}
                data-testid={ADMIN.mapUpload}
              />
              <Button
                onClick={onAddMap}
                disabled={saving}
                data-testid={ADMIN.addMapBtn}
              >
                <Plus size={14} /> Add Map
              </Button>
              <div className="space-y-2" data-testid={ADMIN.mapsList}>
                {(draft.shopMaps || []).map((m, idx) => (
                  <div
                    key={m.url || `map-${idx}`}
                    className="flex items-center gap-2 border rounded p-2"
                  >
                    <img
                      src={resolveMediaUrl(m.url)}
                      alt=""
                      className="h-12 w-20 object-cover rounded"
                    />
                    <span className="text-xs flex-1 truncate">{m.url}</span>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => onDeleteMap(idx)}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>

            {/* Prize Imgs + Links */}
            <TabsContent value="prize" className="space-y-3 pt-4">
              <div className="space-y-2">
                <Label>Millennium Millionaire</Label>
                <Input
                  type="file"
                  accept="image/*"
                  ref={millRef}
                  data-testid={ADMIN.millImg}
                />
                <Input
                  type="text"
                  placeholder="External link"
                  value={draft.millenniumLink || ""}
                  onChange={(e) =>
                    setDraft({ ...draft, millenniumLink: e.target.value })
                  }
                  data-testid={ADMIN.millLink}
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Luxury Car</Label>
                <Input
                  type="file"
                  accept="image/*"
                  ref={luxRef}
                  data-testid={ADMIN.luxCarImg}
                />
                <Input
                  type="text"
                  placeholder="External link"
                  value={draft.luxuryCarLink || ""}
                  onChange={(e) =>
                    setDraft({ ...draft, luxuryCarLink: e.target.value })
                  }
                  data-testid={ADMIN.luxCarLink}
                />
              </div>
              <Separator />
              <div className="space-y-2">
                <Label>Motorcycle</Label>
                <Input
                  type="file"
                  accept="image/*"
                  ref={motoRef}
                  data-testid={ADMIN.motoImg}
                />
                <Input
                  type="text"
                  placeholder="External link"
                  value={draft.motorcycleLink || ""}
                  onChange={(e) =>
                    setDraft({ ...draft, motorcycleLink: e.target.value })
                  }
                  data-testid={ADMIN.motoLink}
                />
              </div>
              <Button
                onClick={onSavePrize}
                disabled={saving}
                data-testid={ADMIN.savePrizeImgs}
              >
                <Save size={14} /> Save Prize Images & Links
              </Button>
            </TabsContent>

            {/* Register */}
            <TabsContent value="reg" className="space-y-3 pt-4">
              <Label>Register Link URL</Label>
              <Input
                type="text"
                value={draft.registerLink || ""}
                onChange={(e) =>
                  setDraft({ ...draft, registerLink: e.target.value })
                }
                data-testid={ADMIN.registerLink}
                placeholder="https://..."
              />
              <Label>Register Background Image / Video</Label>
              <Input
                type="file"
                accept="image/*,video/*"
                ref={regMediaRef}
                data-testid={ADMIN.registerMedia}
              />
              <Button
                onClick={onSaveRegister}
                disabled={saving}
                data-testid={ADMIN.saveRegisterBtn}
              >
                <Save size={14} /> Save Register
              </Button>
              <Separator />
              <div className="space-y-2" data-testid="admin-subscribe-section">
                <Label className="block text-sm font-semibold">Subscribe Popup Modal</Label>
                <Label className="block text-xs">Subscribe Link <span className="text-red-600">*</span></Label>
                <Input
                  type="url"
                  required
                  value={draft.subscribeLink || ""}
                  onChange={(e) => setDraft({ ...draft, subscribeLink: e.target.value })}
                  placeholder="https://..."
                  data-testid="admin-subscribe-link"
                />
                <Label className="block text-xs">Background Image (optional)</Label>
                {(subBgLocal || draft.subscribeBgImage?.url) && (
                  <img
                    src={subBgLocal || resolveMediaUrl(draft.subscribeBgImage.url)}
                    alt="Subscribe popup background preview"
                    className="w-32 aspect-[4/5] object-cover rounded border"
                    data-testid="admin-subscribe-bg-preview"
                  />
                )}
                <Input
                  type="file"
                  accept="image/*"
                  ref={subBgRef}
                  onChange={onSubBgChange}
                  data-testid="admin-subscribe-bg-upload"
                />
                <p className="text-[11px] text-muted-foreground">Shown as a 4:5 portrait popup (image fills it; edges may be trimmed).</p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => onSaveSubscribe(false)}
                    disabled={saving}
                    data-testid="admin-subscribe-save-btn"
                  >
                    <Save size={14} /> Save
                  </Button>
                  <Button
                    variant="outline"
                    onClick={onResetSubscribe}
                    disabled={saving}
                    data-testid="admin-subscribe-reset-btn"
                  >
                    <RotateCcw size={14} /> RESET
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Promos */}
            <TabsContent value="promo" className="space-y-3 pt-4">
              <PromoForm
                promoLoc={promoLoc}
                setPromoLoc={setPromoLoc}
                promoForm={promoForm}
                setPromoForm={setPromoForm}
                promoImgRef={promoImgRef}
                onAddPromo={onAddPromo}
                saving={saving}
              />
              <Separator />
              <PromoList
                promoListTab={promoListTab}
                setPromoListTab={setPromoListTab}
                publishedDepartures={publishedDepartures}
                publishedArrivals={publishedArrivals}
                holdingItems={holdingItems}
                onEditPromo={onEditPromo}
                onRemovePromo={onRemovePromo}
                batchPublishAt={batchPublishAt}
                setBatchPublishAt={setBatchPublishAt}
                holdingEmail={holdingEmail}
                setHoldingEmail={setHoldingEmail}
                emailingHolding={emailingHolding}
                onEmailHolding={onEmailHolding}
                downloadingHolding={downloadingHolding}
                onDownloadHolding={onDownloadHolding}
                holdingEmailError={holdingEmailError}
                selectedHolding={selectedHolding}
                toggleHoldingSelect={toggleHoldingSelect}
                onDuplicateHolding={onDuplicateHolding}
                onScheduleAll={onScheduleAll}
                onPublishAllNow={onPublishAllNow}
                onChangePublishAt={onChangePublishAt}
                onScheduleItem={onScheduleItem}
                onPublishNow={onPublishNow}
                saving={saving}
                persist={persist}
                draft={draft}
              />
            </TabsContent>

            {/* Contact */}
            <TabsContent value="contact" className="space-y-3 pt-4">
              <Label>Phone</Label>
              <Input
                value={draft.contactPhone || ""}
                onChange={(e) =>
                  setDraft({ ...draft, contactPhone: e.target.value })
                }
                data-testid={ADMIN.contactPhone}
              />
              <Label>Toll Free</Label>
              <Input
                value={draft.contactTollFree || ""}
                onChange={(e) =>
                  setDraft({ ...draft, contactTollFree: e.target.value })
                }
                data-testid={ADMIN.contactTollFree}
              />
              <Label>Email</Label>
              <Input
                value={draft.contactEmail || ""}
                onChange={(e) =>
                  setDraft({ ...draft, contactEmail: e.target.value })
                }
                data-testid={ADMIN.contactEmail}
              />
              <Button
                onClick={onSaveContact}
                disabled={saving}
                data-testid={ADMIN.saveContactBtn}
              >
                <Save size={14} /> Save Contact Info
              </Button>
            </TabsContent>

            {/* Analytics */}
            <TabsContent value="analytics" className="space-y-3 pt-4">
              <AnalyticsPanel />
            </TabsContent>
          </Tabs>
        </SheetContent>
      </Sheet>

      {/* Edit Promo Modal — extracted to its own component */}
      <EditPromoDialog
        editing={editing}
        setEditing={setEditing}
        onSave={onSaveEditing}
        editImgRef={editImgRef}
      />
    </>
  );
}
